import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "FiveHeddleWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Specialty Woven Mesh",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Five-Heddle Weave Wire Mesh",
      lead: "Engineered satin weave cloth featuring an asymmetric 1/4 interlacing pattern that produces an ultra-smooth, low-friction surface on one face and robust drainage channels on the reverse.",
      secondaryCta: {
        label: "Specialty Woven Mesh",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Five-heddle stainless steel woven wire mesh structure diagram",
      },
      cardNote: "1/4 Satin Weave Pattern",
      cardLink: {
        label: "Technical Assistance",
        to: "/ContactPage",
      },
      specs: [
        { label: "Weave Pattern", value: "1 / 4 Satin Weave" },
        { label: "Working Face", value: "Planar Smooth" },
        { label: "Standard Alloys", value: "304, 316, 316L" },
        { label: "Mesh Count", value: "10 – 325 Mesh" },
      ],
    },
    overview: {
      eyebrow: "Satin Weave Geometry",
      heading: "Asymmetric Surface Construction for Rapid Cake Release and Low Flow Resistance",
      paragraphs: [
        "Five-heddle weave (also referred to as 5-harness satin weave) is produced by passing each warp wire alternately over four shute wires and under one. This specific harness sequence arranges long wire knuckles across the process-facing surface, creating a planar, smooth contact boundary with minimal particle entrapment crevices.",
        "On the reverse side, the perpendicular interlacings form defined parallel ridges that act as structural drainage channels. When liquid passes through the mesh, filtrate collects rapidly within these lower channels while the retained solid cake rests lightly on the flat face without becoming mechanically locked into the aperture interstices.",
        "This asymmetric architecture makes five-heddle weave especially effective in pressure leaf filters, rotary drum filters, centrifugal discharge separators, and underdrain filtration plates where automated scrapers, backwash pulses, or gas blowback dislodge cake easily without blinding.",
      ],
      keyPoints: [
        "Planar upper surface minimizes cake bridging and allows clean scraper or blowback cake detachment",
        "Reverse side ridges create natural drainage pathways that reduce flow resistance through the support grid",
        "Woven in corrosion-resistant AISI 304, 316, and 316L for stable mechanical performance across continuous cycles",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Five-heddle weave stainless steel wire cloth microscopic structure",
      },
    },
    specTable: {
      heading: "Representative Five-Heddle Mesh Specifications",
      intro: "Standard dimensional ranges for five-heddle woven wire cloth in stainless steel alloys.",
      columns: [
        { key: "mesh", label: "Mesh Count (Warp x Shute)", align: "left" },
        { key: "warpWire", label: "Warp Wire Ø (mm)", align: "right" },
        { key: "shuteWire", label: "Shute Wire Ø (mm)", align: "right" },
        { key: "thickness", label: "Nominal Thickness (mm)", align: "right" },
        { key: "primaryRole", label: "Primary Operating Focus", align: "left" },
      ],
      rows: [
        {
          mesh: "14 x 10 Mesh",
          warpWire: "0.45",
          shuteWire: "0.40",
          thickness: "0.95",
          primaryRole: "Coarse pre-coat support & drainage grids",
        },
        {
          mesh: "24 x 18 Mesh",
          warpWire: "0.35",
          shuteWire: "0.30",
          thickness: "0.75",
          primaryRole: "Vertical pressure leaf filtration",
        },
        {
          mesh: "40 x 30 Mesh",
          warpWire: "0.25",
          shuteWire: "0.22",
          thickness: "0.55",
          primaryRole: "Chemical cake discharge & pharmaceutical filtering",
        },
        {
          mesh: "60 x 40 Mesh",
          warpWire: "0.18",
          shuteWire: "0.15",
          thickness: "0.40",
          primaryRole: "Fine clarification and rotary drum surface screens",
        },
      ],
      caption: "Specifications conform to ASTM E2016 tolerances. Alternate warp/shute combinations and custom alloy rolls are manufactured to order.",
    },
    features: {
      heading: "Performance Advantages in Cake Filtration",
      intro: "Core operational benefits provided by the 5-harness satin weave structure.",
      items: [
        {
          title: "Low Mechanical Adhesion",
          text: "The flat contact plane prevents particles from embedding deeply between adjacent wires, significantly speeding up wash and discharge cycles.",
        },
        {
          title: "Integral Drainage Ribs",
          text: "Reverse-face knuckles create high-permeability micro-conduits, preventing hydraulic choking against supporting plates or frames.",
        },
        {
          title: "Mechanical Durability",
          text: "The 1/4 weave structure distributes localized tensile stress across multiple interlocking nodes, resisting deformation under cyclic differential pressures.",
        },
        {
          title: "Precise Custom Converting",
          text: "Supplied in roll form, custom-cut rectangular sheets for filter leaves, or pre-welded cylindrical sleeves with finished selvage edges.",
        },
      ],
    },
    related: {
      heading: "Related Specialty Weaves & Sibling Architectures",
      intro: "Compare five-heddle weave with our broader specialty woven wire mesh family and sibling configurations.",
      cards: [
        {
          title: "Specialty Woven Mesh",
          text: "Explore the complete family of advanced woven wire cloth constructions engineered for critical industrial applications.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Parent Category",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Specialty precision stainless steel woven wire mesh",
          },
        },
        {
          title: "Heavy-Duty Woven Mesh",
          text: "Pre-crimped lock-crimp and flat-top screening cloth built from heavy-gauge wire for aggregate sizing and severe mechanical abrasion.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Heavy Gauge",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Industrial heavy-duty pre-crimped woven wire mesh",
          },
        },
        {
          title: "Custom Woven Mesh",
          text: "Custom aperture geometries, special warp/shute ratios, and specialized alloys tailored to equipment OEM specifications.",
          to: "/CustomWovenMeshPage",
          badge: "Tailored Geometry",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Precision custom engineered stainless steel woven wire mesh",
          },
        },
      ],
    },
    cta: {
      id: "five-heddle-cta",
      eyebrow: "Filtration Engineering",
      heading: "Inquire About Five-Heddle Weave Wire Mesh",
      lead: "Contact our technical specialists to confirm mesh counts, sheet dimensions, and alloy grades tailored to your pressure leaf or cake filtration system.",
      secondaryLabel: "Specialty Weaves Overview",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Standard rolls and custom cut filter leaves available upon request.",
    },
  },

  zh: {
    breadcrumbKey: "FiveHeddleWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "特种编织丝网",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "五综编织丝网",
      lead: "采用 1/4 斜缎纹织造结构，正面形成平整光滑、低摩擦阻力的工作面，背面形成清晰刚性的排液支撑通道，特别适用于滤饼快速剥离。",
      secondaryCta: {
        label: "特种编织网总览",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "五综不锈钢编织丝网微观织造结构示意图",
      },
      cardNote: "1/4 斜缎纹结构",
      cardLink: {
        label: "技术咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "编织形式", value: "1 / 4 沉浮缎纹" },
        { label: "工作表面", value: "单面高平整度" },
        { label: "常用材质", value: "304, 316, 316L" },
        { label: "目数范围", value: "10 – 325 目" },
      ],
    },
    overview: {
      eyebrow: "缎纹织造几何",
      heading: "非对称表面几何结构：实现极速滤饼剥离与低流阻排液",
      paragraphs: [
        "五综编织丝网（又称五综缎纹网）每根经丝依次跨越四根纬丝并在第五根纬丝下方穿过。这种特定的提综循环使正面呈现连续平滑的金属导向面，极大减少了颗粒嵌顿的死角与微观缝隙。",
        "在丝网背面，交织形成的经纬突起构成了排列规整的微型导流槽。在固液分离过程中，截留固体颗粒悬浮松散地附着在平整的上表面，而滤液则顺畅汇入下表面导流槽排出，避免了因毛细吸附造成的滤层流阻升高。",
        "这种非对称结构使五综网在立式或卧式压力叶滤机、转鼓过滤机、离心卸料机以及底部排液板中表现优异。在自动刮刀、反吹气体或高压反洗脉冲作用下，滤饼极易整块脱落，大幅减少洗网停机频率。",
      ],
      keyPoints: [
        "正面平整光滑，颗粒不易在孔隙中深层卡滞，利于刮刀与反吹彻底卸饼",
        "反面凸棱形成天然导流槽，防止滤网直接贴死支撑板导致过流阻力上升",
        "采用 AISI 304、316 及 316L 优质奥氏体不锈钢，耐受循环交变压力疲劳",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "五综编织不锈钢丝网微观编织结构放大",
      },
    },
    specTable: {
      heading: "典型五综编织网规格参数",
      intro: "常用五综不锈钢编织丝网的标准物理几何参数。",
      columns: [
        { key: "mesh", label: "目数规格 (经 x 纬)", align: "left" },
        { key: "warpWire", label: "经丝丝径 (mm)", align: "right" },
        { key: "shuteWire", label: "纬丝丝径 (mm)", align: "right" },
        { key: "thickness", label: "公称厚度 (mm)", align: "right" },
        { key: "primaryRole", label: "主要应用场景", align: "left" },
      ],
      rows: [
        {
          mesh: "14 x 10 目",
          warpWire: "0.45",
          shuteWire: "0.40",
          thickness: "0.95",
          primaryRole: "预涂层底层支撑与粗粒排液",
        },
        {
          mesh: "24 x 18 目",
          warpWire: "0.35",
          shuteWire: "0.30",
          thickness: "0.75",
          primaryRole: "立式压力叶滤机过滤板主过滤层",
        },
        {
          mesh: "40 x 30 目",
          warpWire: "0.25",
          shuteWire: "0.22",
          thickness: "0.55",
          primaryRole: "精细化工滤饼回收与制药固液脱水",
        },
        {
          mesh: "60 x 40 目",
          warpWire: "0.18",
          shuteWire: "0.15",
          thickness: "0.40",
          primaryRole: "澄清过滤与转鼓真空吸滤机表面网",
        },
      ],
      caption: "尺寸公差符合 ASTM E2016 规范。可按技术要求定制经纬配比与特殊合金规格卷料。",
    },
    features: {
      heading: "滤饼分离核心性能优势",
      intro: "1/4 缎纹几何结构在工业连续过滤中的技术特点。",
      items: [
        {
          title: "极低表面附着阻力",
          text: "平滑连续的工作面避免了颗粒深入夹持在两丝之间，大幅加快清洗与反洗卸料节奏。",
        },
        {
          title: "背面集成导流微槽",
          text: "反面凸棱为滤液快速横向扩散排出提供了通道，避免滤网吸附贴死在多孔支撑板上。",
        },
        {
          title: "优异的抗拉与耐压性",
          text: "交织点力学分散均匀，能有效吸收周期性开机闭机产生的压力脉冲，防止网面永久凹陷。",
        },
        {
          title: "高精度成形加工",
          text: "提供整卷卷料、预剪裁定制尺寸叶片网、冲压滤片及带包边和点焊加固的成套过滤组件。",
        },
      ],
    },
    related: {
      heading: "相关特种编织结构与同级产品",
      intro: "对比五综编织网与特种编织系列中的其他架构，选择最匹配工艺的丝网方案。",
      cards: [
        {
          title: "特种编织丝网",
          text: "了解特种编织丝网完整分类，涵盖非对称缎纹、高抗冲轧花及定制几何织造。",
          to: "/SpecialtyWovenMeshPage",
          badge: "上级分类",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "特种精密不锈钢编织丝网",
          },
        },
        {
          title: "重型编织网",
          text: "采用粗线径预弯成型定型结构，专为矿业、骨料筛分等强冲击磨损环境设计。",
          to: "/HeavyDutyWovenMeshPage",
          badge: "重载高刚度",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "工业重型不锈钢预弯轧花编织丝网",
          },
        },
        {
          title: "定制编织网",
          text: "针对非标开孔要求、特殊经纬配比及复杂设备工况量身设计的工程编织丝网。",
          to: "/CustomWovenMeshPage",
          badge: "非标定制",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "高精度定制不锈钢编织丝网",
          },
        },
      ],
    },
    cta: {
      id: "five-heddle-cta",
      eyebrow: "过滤选型咨询",
      heading: "咨询五综编织丝网技术参数与样品方案",
      lead: "我们的技术工程师可根据您的叶滤机型号、截留介质及滤网尺寸要求，提供详细的规格方案核算。",
      secondaryLabel: "返回特种编织总览",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "可按需提供标准卷料或成型加工叶滤机筛片。",
    },
  },

  es: {
    breadcrumbKey: "FiveHeddleWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Malla de alambre especial",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Malla tejida de cinco lizos (Five-Heddle)",
      lead: "Tejido de raso asimétrico de ligamento 1/4 que proporciona una cara de trabajo ultra lisa de baja fricción y canales de drenaje integrados en el reverso.",
      secondaryCta: {
        label: "Malla especial general",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Diagrama de estructura de malla tejida de cinco lizos en acero inoxidable",
      },
      cardNote: "Ligamento raso 1/4",
      cardLink: {
        label: "Asistencia técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Ligamento", value: "1 / 4 Sarga-Raso" },
        { label: "Superficie de trabajo", value: "Plana y lisa" },
        { label: "Aleaciones estándar", value: "304, 316, 316L" },
        { label: "Conteo de malla", value: "10 – 325 Mesh" },
      ],
    },
    overview: {
      eyebrow: "Geometría de tejido en raso",
      heading: "Estructura asimétrica para desprendimiento rápido de torta y drenaje fluido",
      paragraphs: [
        "El tejido de cinco lizos se obtiene haciendo pasar cada hilo de urdimbre alternativamente por encima de cuatro hilos de trama y por debajo de uno. Esta secuencia coloca tramos de alambre largos en la cara exterior, creando una superficie lisa y plana casi sin hendiduras microscópicas.",
        "En la cara inferior, los cruces perpendiculares crean un relieve de nervaduras paralelas que funcionan como canales de drenaje. Los sólidos retenidos se apoyan con poca adherencia en la superficie plana, mientras que el líquido filtrado fluye rápidamente hacia las vías inferiores sin encontrar resistencia hidráulica excesiva.",
        "Esta estructura asimétrica resulta idónea para filtros de hojas a presión horizontales o verticales, filtros de tambor rotativo, centrífugas y placas de soporte, donde los rascadores automáticos o impulsos de contralavado expulsan la torta con rapidez y sin cegar la malla.",
      ],
      keyPoints: [
        "Superficie plana superior que facilita la descarga de sólidos sin atrapamiento mecánico de partículas",
        "Canales de drenaje en el reverso que reducen la resistencia al flujo contra placas perforadas",
        "Fabricada en aceros inoxidables AISI 304, 316 y 316L con alta resistencia a fatiga por ciclos de presión",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Estructura microscópica de tela metálica de cinco lizos",
      },
    },
    specTable: {
      heading: "Especificaciones representativas de tejido de cinco lizos",
      intro: "Rangos estándar de malla tejida de 5 lizos en calidades de acero inoxidable.",
      columns: [
        { key: "mesh", label: "Malla (Urdimbre x Trama)", align: "left" },
        { key: "warpWire", label: "Alambre urdimbre (mm)", align: "right" },
        { key: "shuteWire", label: "Alambre trama (mm)", align: "right" },
        { key: "thickness", label: "Espesor nominal (mm)", align: "right" },
        { key: "primaryRole", label: "Aplicación preferente", align: "left" },
      ],
      rows: [
        {
          mesh: "14 x 10 Mesh",
          warpWire: "0.45",
          shuteWire: "0.40",
          thickness: "0.95",
          primaryRole: "Soporte de precapa y drenaje grueso",
        },
        {
          mesh: "24 x 18 Mesh",
          warpWire: "0.35",
          shuteWire: "0.30",
          thickness: "0.75",
          primaryRole: "Hojas de filtración a presión vertical",
        },
        {
          mesh: "40 x 30 Mesh",
          warpWire: "0.25",
          shuteWire: "0.22",
          thickness: "0.55",
          primaryRole: "Descarga de torta química y farmacéutica",
        },
        {
          mesh: "60 x 40 Mesh",
          warpWire: "0.18",
          shuteWire: "0.15",
          thickness: "0.40",
          primaryRole: "Clarificación fina y pantallas de tambor rotativo",
        },
      ],
      caption: "Tolerancias conformes con ASTM E2016. Posibilidad de configuraciones especiales bajo pedido.",
    },
    features: {
      heading: "Ventajas en procesos de filtración de torta",
      intro: "Beneficios de proceso aportados por el ligamento de 5 lizos.",
      items: [
        {
          title: "Baja adherencia superficial",
          text: "El plano de contacto continuo evita que los sólidos queden encajados entre hilos contiguos, acelerando la limpieza.",
        },
        {
          title: "Canales de drenaje posteriores",
          text: "Las nervaduras inferiores impiden que la malla se bloquee contra las placas de soporte, manteniendo el caudal.",
        },
        {
          title: "Resistencia mecánica uniforme",
          text: "El patrón de entrelazado distribuye las fuerzas de presión cíclicas entre múltiples puntos, evitando deformaciones.",
        },
        {
          title: "Conformado personalizado",
          text: "Suministro en rollos, telas cortadas para filtros de hojas, discos troquelados o manguitos con bordes soldados.",
        },
      ],
    },
    related: {
      heading: "Mallas especiales y variantes relacionadas",
      intro: "Consulte el resto de tipologías dentro de nuestro programa de mallas de alambre especiales.",
      cards: [
        {
          title: "Malla tejida especial",
          text: "Visión general de las construcciones avanzadas para aplicaciones mecánicas y químicas complejas.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Categoría principal",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Malla de alambre tejida especial de acero inoxidable",
          },
        },
        {
          title: "Malla tejida de servicio pesado",
          text: "Malla de alambre grueso preondulado para cribado de áridos y ambientes con severo desgaste por abrasión.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Servicio pesado",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Malla preondulada para servicio pesado",
          },
        },
        {
          title: "Malla tejida a medida",
          text: "Geometrías de apertura a medida, combinaciones especiales de alambre y aleaciones bajo pedido.",
          to: "/CustomWovenMeshPage",
          badge: "Configuración a medida",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Malla tejida de acero inoxidable a medida",
          },
        },
      ],
    },
    cta: {
      id: "five-heddle-cta",
      eyebrow: "Consulta técnica",
      heading: "Solicitar especificaciones para malla de cinco lizos",
      lead: "Consulte a nuestro departamento técnico sobre conteos de malla, medidas de corte y aleaciones para su equipo de filtración.",
      secondaryLabel: "Ver mallas especiales",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Hojas cortadas a medida y rollos disponibles bajo especificación.",
    },
  },

  de: {
    breadcrumbKey: "FiveHeddleWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Spezial-Drahtgewebe",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Fünfschaftgewebe (Five-Heddle)",
      lead: "Asymmetrische 1/4-Atlasbindung mit extrem glatter, reibungsarmer Oberseite zur schnellen Filterkuchenablösung und strömungsgünstigen Drainagerippen auf der Rückseite.",
      secondaryCta: {
        label: "Spezialgewebe Übersicht",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Strukturdiagramm für 5-Schaft-Edelstahldrahtgewebe",
      },
      cardNote: "1/4-Atlas-Bindungsmuster",
      cardLink: {
        label: "Technischer Support",
        to: "/ContactPage",
      },
      specs: [
        { label: "Bindung", value: "1 / 4 Köper-Atlas" },
        { label: "Arbeitsseite", value: "Plan und glatt" },
        { label: "Standardlegierungen", value: "304, 316, 316L" },
        { label: "Maschenbereich", value: "10 – 325 Mesh" },
      ],
    },
    overview: {
      eyebrow: "Atlas-Gewebegeometrie",
      heading: "Asymmetrische Oberflächenstruktur für leichten Kuchenabwurf und minimalen Strömungswiderstand",
      paragraphs: [
        "Fünfschaftgewebe (auch 5-Schaft-Atlasgewebe) wird gewebt, indem jeder Kettfaden abwechselnd über vier Schussfäden hinweg und unter einem Schussfaden hindurchgeführt wird. Diese Bindungsfolge platziert lange Drahtsegmente auf der prozesszugewandten Oberseite und bildet eine ebene Kontaktfläche mit minimalen Verhakungspunkten.",
        "Auf der Unterseite bilden die Kreuzungspunkte ausgeprägte parallele Rippen, die als Drainagerillen wirken. Das Filtrat fließt durch diese Kanäle rasch ab, während der Feststoffkuchen locker auf der glatten Oberseite aufliegt und nicht mechanisch in die Maschenöffnungen eingedrückt wird.",
        "Diese Bauart eignet sich vor allem für Druckblattfilter, Vakuum-Trommelfilter, Zentrifugen und Drainageträger. Unter dem Einfluss von Abstreifern, Gasrückblasen oder Rückspülimpulsen löst sich der Kuchen sauber und rückstandsfrei vom Gewebe.",
      ],
      keyPoints: [
        "Planare Oberfläche verhindert Kuchenverblockung und ermöglicht sauberen Abwurf bei Rückspülung",
        "Rückseitige Rillen bilden Strömungskanäle, die Druckverluste am Stützboden minimieren",
        "Gefertigt aus austenitischen Edelstählen AISI 304, 316 und 316L mit hoher Wechsellastfestigkeit",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Mikrostruktur von Fünfschaft-Drahtgewebe aus Edelstahl",
      },
    },
    specTable: {
      heading: "Typische Spezifikationen für Fünfschaftgewebe",
      intro: "Geometrische Richtwerte für Fünfschaft-Edelstahldrahtgewebe.",
      columns: [
        { key: "mesh", label: "Maschenzahl (Kette x Schuss)", align: "left" },
        { key: "warpWire", label: "Kettfahrdraht Ø (mm)", align: "right" },
        { key: "shuteWire", label: "Schussdraht Ø (mm)", align: "right" },
        { key: "thickness", label: "Nenndicke (mm)", align: "right" },
        { key: "primaryRole", label: "Einsatzschwerpunkt", align: "left" },
      ],
      rows: [
        {
          mesh: "14 x 10 Mesh",
          warpWire: "0,45",
          shuteWire: "0,40",
          thickness: "0,95",
          primaryRole: "Precoat-Stützschicht & Grobdrainage",
        },
        {
          mesh: "24 x 18 Mesh",
          warpWire: "0,35",
          shuteWire: "0,30",
          thickness: "0,75",
          primaryRole: "Vertikale Druckblattfiltration",
        },
        {
          mesh: "40 x 30 Mesh",
          warpWire: "0,25",
          shuteWire: "0,22",
          thickness: "0,55",
          primaryRole: "Chemischer Kuchenabwurf & Pharmafiltration",
        },
        {
          mesh: "60 x 40 Mesh",
          warpWire: "0,18",
          shuteWire: "0,15",
          thickness: "0,40",
          primaryRole: "Feinklärung & Trommelfilterbespannungen",
        },
      ],
      caption: "Toleranzen nach ASTM E2016. Zusätzliche Draht- und Maschenkombinationen werden auftragsbezogen gefertigt.",
    },
    features: {
      heading: "Prozessvorteile in der Kuchenfiltration",
      intro: "Konstruktionsmerkmale des 5-Schaft-Atlasgewebes.",
      items: [
        {
          title: "Minimale Kuchenhaftung",
          text: "Die glatte Ebene verhindert ein Festsetzen von Feststoffpartikeln in den Drahtzwischenräumen und verkürzt Waschzeiten.",
        },
        {
          title: "Integrierte Drainagekanäle",
          text: "Rückseitige Rippen ermöglichen einen ungehinderten Filtratabfluss und verhindern das festsitzende Ansaugen an Tragkörper.",
        },
        {
          title: "Gleichmäßige Druckbelastbarkeit",
          text: "Die Maschenbindung verteilt Differenzdruckkräfte auf zahlreiche Knotenpunkte und schützt vor bleibender Verformung.",
        },
        {
          title: "Präzise Konfektionierung",
          text: "Lieferung als Rollenware, passgenau zugeschnittene Filterblätter, Ronden oder zylindrische Hülsen.",
        },
      ],
    },
    related: {
      heading: "Verwandte Spezialgewebe",
      intro: "Vergleichen Sie das Fünfschaftgewebe mit weiteren Webarten unseres Spezialprogramms.",
      cards: [
        {
          title: "Spezial-Drahtgewebe",
          text: "Gesamte Palette spezialisierter Drahtgewebe für fordernde industrielle Trennverfahren.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Hauptkategorie",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Präzisions-Spezialdrahtgewebe aus Edelstahl",
          },
        },
        {
          title: "Schwerlast-Drahtgewebe",
          text: "Dickdrähtige Schlosskröpfung für abrasive Schüttgüter und hohe mechanische Stoßkräfte.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Schwere Drahtstärken",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Vorgekröpftes Schwerlastgewebe aus Edelstahl",
          },
        },
        {
          title: "Kundenspezifisches Gewebe",
          text: "Maßgeschneiderte Kett-/Schussgeometrien und Sonderabmessungen für spezifische Anlagenkonstruktionen.",
          to: "/CustomWovenMeshPage",
          badge: "Individuelle Geometrie",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Kundenspezifisches Edelstahl-Drahtgewebe",
          },
        },
      ],
    },
    cta: {
      id: "five-heddle-cta",
      eyebrow: "Filtrationsberatung",
      heading: "Fünfschaftgewebe Spezifikationen anfragen",
      lead: "Sprechen Sie mit unseren Technikern über passende Maschenweiten, Zuschnittmaße und Edelstahllegierungen für Ihren Filter.",
      secondaryLabel: "Spezialgewebe ansehen",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Standardrollen und passgenaue Filterblätter auf Anfrage erhältlich.",
    },
  },

  fr: {
    breadcrumbKey: "FiveHeddleWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Toile métallique spéciale",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Tissage 5 cadres (Five-Heddle)",
      lead: "Armure satin asymétrique 1/4 offrant une face de travail ultra lisse à faible frottement et des canaux de drainage longitudinaux au dos.",
      secondaryCta: {
        label: "Toiles spéciales générales",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Schéma structurel de toile métallique en acier inoxydable à cinq lames",
      },
      cardNote: "Armure satin 1/4",
      cardLink: {
        label: "Support technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Armure", value: "1 / 4 Sergé-Satin" },
        { label: "Face de travail", value: "Plane et lisse" },
        { label: "Alliages standards", value: "304, 316, 316L" },
        { label: "Nombre de mailles", value: "10 – 325 Mesh" },
      ],
    },
    overview: {
      eyebrow: "Géométrie en armure satin",
      heading: "Structure de surface asymétrique pour décolmatage rapide et faible perte de charge",
      paragraphs: [
        "Le tissage 5 cadres (ou armure satin à 5 lames) est confectionné en faisant passer chaque fil de chaîne au-dessus de quatre fils de trame puis sous un fil. Cette armure place de longs segments de fil sur la face orientée procédé, formant un plan lisse sans aspérités de piégeage pour les particules solides.",
        "Sur la face inférieure, les croisements dessinent des nervures parallèles agissant comme des canaux de drainage continus. Le liquide filtré s'évacue rapidement le long de ces sillons sans colmater le support, tandis que le gâteau de matière repose légèrement sur la surface plane sans s'incruster dans les mailles.",
        "Cette structure est particulièrement avantageuse sur les filtres à plaques sous pression horizontaux ou verticaux, les filtres à tambour rotatif, les essoreuses centrifuges et les plaques de fondation, où racleurs et contre-courants de gaz décrochent le gâteau en un minimum de temps.",
      ],
      keyPoints: [
        "Surface supérieure plane facilitant l'évacuation intégrale du gâteau de filtration sans colmatage",
        "Nervures inférieures formant des canaux de drainage qui réduisent la résistance hydraulique d'écoulement",
        "Fabriquée en inox AISI 304, 316 et 316L résistant aux cycles répétitifs de mise en pression",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Vue rapprochée de toile métallique satin 5 cadres en inox",
      },
    },
    specTable: {
      heading: "Spécifications types du tissage 5 cadres",
      intro: "Paramètres géométriques usuels pour toiles en acier inoxydable 5 cadres.",
      columns: [
        { key: "mesh", label: "Mailles (Chaîne x Trame)", align: "left" },
        { key: "warpWire", label: "Fil de chaîne (mm)", align: "right" },
        { key: "shuteWire", label: "Fil de trame (mm)", align: "right" },
        { key: "thickness", label: "Épaisseur nominale (mm)", align: "right" },
        { key: "primaryRole", label: "Domaine préférentiel", align: "left" },
      ],
      rows: [
        {
          mesh: "14 x 10 Mesh",
          warpWire: "0,45",
          shuteWire: "0,40",
          thickness: "0,95",
          primaryRole: "Support de précouche et drainage grossier",
        },
        {
          mesh: "24 x 18 Mesh",
          warpWire: "0,35",
          shuteWire: "0,30",
          thickness: "0,75",
          primaryRole: "Plateaux de filtres sous pression verticaux",
        },
        {
          mesh: "40 x 30 Mesh",
          warpWire: "0,25",
          shuteWire: "0,22",
          thickness: "0,55",
          primaryRole: "Extraction de gâteau chimique et pharmacie",
        },
        {
          mesh: "60 x 40 Mesh",
          warpWire: "0,18",
          shuteWire: "0,15",
          thickness: "0,40",
          primaryRole: "Clarification fine et toiles pour tambours rotatifs",
        },
      ],
      caption: "Tolérances de fabrication selon ASTM E2016. Autres rapports de fils réalisables sur demande.",
    },
    features: {
      heading: "Avantages opérationnels en filtration sur gâteau",
      intro: "Propriétés déterminantes de l'armure satin 5 lames.",
      items: [
        {
          title: "Adhérence minimale du gâteau",
          text: "La planéité continue de la face de contact évite l'encastrement des particules solides entre les spires de fil.",
        },
        {
          title: "Micro-canaux de drainage intégrés",
          text: "Les ondulations dorsales évitent l'aspiration à plat sur les plaques supports, garantissant le débit de filtrat.",
        },
        {
          title: "Résistance aux pressions cycliques",
          text: "La répartition des croisements absorbe efficacement les alternances de charge sans déformation permanente.",
        },
        {
          title: "Confection adaptée aux carters",
          text: "Fourniture en rouleaux, feuilles découpées pour filtres feuilles, disques estampés ou viroles soudées.",
        },
      ],
    },
    related: {
      heading: "Toiles spéciales associées",
      intro: "Comparez le tissage 5 cadres avec les autres architectures de notre gamme technique.",
      cards: [
        {
          title: "Toiles métalliques spéciales",
          text: "Découvrez toutes nos toiles techniques tissées pour séparations mécaniques et chimiques exigeantes.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Catégorie mère",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Toile tissée inox de haute précision pour filtration spéciale",
          },
        },
        {
          title: "Toile tissée renforcée",
          text: "Toile pré-ondulée à gros fils pour le criblage de charges lourdes et minérales abrasives.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Gros diamètres",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Toile métallique tissée pré-ondulée pour service lourd",
          },
        },
        {
          title: "Toile tissée sur mesure",
          text: "Combinaisons spécifiques d'ouvertures, de fils et d'alliages adaptées à vos cahiers des charges OEM.",
          to: "/CustomWovenMeshPage",
          badge: "Fabrication sur mesure",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Structure de toile métallique sur mesure en acier inoxydable",
          },
        },
      ],
    },
    cta: {
      id: "five-heddle-cta",
      eyebrow: "Conseil technique",
      heading: "Consulter pour du tissage 5 cadres (Five-Heddle)",
      lead: "Prenez contact avec nos ingénieurs pour définir le nombre de mailles, le format de feuille et l'inox adapté à votre filtre.",
      secondaryLabel: "Voir les toiles spéciales",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Feuilles découpées et rouleaux entiers livrables selon commande.",
    },
  },

  pt: {
    breadcrumbKey: "FiveHeddleWeave",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Telas metálicas especiais",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Tecelagem de 5 liços (Five-Heddle)",
      lead: "Trama de cetim assimétrica de padrão 1/4 que produz uma face de contato ultraplana de baixo atrito e canais estruturados de drenagem no verso.",
      secondaryCta: {
        label: "Telas especiais geral",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Diagrama estrutural de malha de arame tecida de cinco liços em aço inoxidável",
      },
      cardNote: "Trama cetim 1/4",
      cardLink: {
        label: "Suporte de engenharia",
        to: "/ContactPage",
      },
      specs: [
        { label: "Entrelaçamento", value: "1 / 4 Sarja-Cetim" },
        { label: "Face de contato", value: "Plana e lisa" },
        { label: "Ligas padrão", value: "304, 316, 316L" },
        { label: "Contagem de malha", value: "10 – 325 Mesh" },
      ],
    },
    overview: {
      eyebrow: "Geometria em padrão cetim",
      heading: "Construção assimétrica para descarga acelerada da torta e baixa resistência hidráulica",
      paragraphs: [
        "A tecelagem de cinco liços (também conhecida como cetim de 5 liços) é produzida passando cada arame de urdume alternadamente por cima de quatro arames de trama e por baixo de um. Essa sequência posiciona segmentos longos de arame na superfície voltada ao processo, criando um plano contínuo e nivelado.",
        "No verso da tela, os cruzamentos perpendiculares formam nervuras paralelas que funcionam como canais de drenagem. A torta sólida repousa com baixa aderência sobre a face superior, enquanto o filtrado é direcionado rapidamente para os canais inferiores sem encontrar resistência que provoque colmatação precoce.",
        "Essa estrutura é especialmente recomendada para filtros de folhas sob pressão verticais ou horizontais, tambores rotativos a vácuo, centrífugas de descarga contínua e placas de drenagem, onde raspadores e pulsos de ar desprendem a torta com máxima facilidade.",
      ],
      keyPoints: [
        "Superfície plana superior que evita o aprisionamento de sólidos e agiliza a descarga mecânica",
        "Nervuras inferiores que atuam como canais naturais de drenagem junto a placas de suporte",
        "Confeccionada em aços inoxidáveis AISI 304, 316 e 316L com alta durabilidade sob pressões cíclicas",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
        alt: "Estrutura microscópica de tela tecida de cinco liços",
      },
    },
    specTable: {
      heading: "Especificações típicas de tecelagem de cinco liços",
      intro: "Dimensões padrão de telas de aço inoxidável em tecelagem de 5 liços.",
      columns: [
        { key: "mesh", label: "Malha (Urdume x Trama)", align: "left" },
        { key: "warpWire", label: "Arame urdume (mm)", align: "right" },
        { key: "shuteWire", label: "Arame trama (mm)", align: "right" },
        { key: "thickness", label: "Espessura nominal (mm)", align: "right" },
        { key: "primaryRole", label: "Aplicação principal", align: "left" },
      ],
      rows: [
        {
          mesh: "14 x 10 Mesh",
          warpWire: "0,45",
          shuteWire: "0,40",
          thickness: "0,95",
          primaryRole: "Suporte de pré-capa e drenagem pesada",
        },
        {
          mesh: "24 x 18 Mesh",
          warpWire: "0,35",
          shuteWire: "0,30",
          thickness: "0,75",
          primaryRole: "Filtros de folhas sob pressão vertical",
        },
        {
          mesh: "40 x 30 Mesh",
          warpWire: "0,25",
          shuteWire: "0,22",
          thickness: "0,55",
          primaryRole: "Descarga de torta química e farmácia",
        },
        {
          mesh: "60 x 40 Mesh",
          warpWire: "0,18",
          shuteWire: "0,15",
          thickness: "0,40",
          primaryRole: "Clarificação fina e telas de tambor rotativo",
        },
      ],
      caption: "Tolerâncias de acordo com ASTM E2016. Outras combinações de diâmetros de arame fornecidas sob encomenda.",
    },
    features: {
      heading: "Vantagens operacionais na filtração de torta",
      intro: "Benefícios proporcionados pela arquitetura de cetim de 5 liços.",
      items: [
        {
          title: "Desprendimento rápido de sólidos",
          text: "A planicidade da face impede que partículas se prendam entre os fios, encurtando ciclos de lavagem.",
        },
        {
          title: "Canais de escoamento no verso",
          text: "As saliências dorsais garantem a passagem contínua do filtrado sem bloqueio contra as placas perfuradas.",
        },
        {
          title: "Estabilidade mecânica sob ciclos",
          text: "A amarração entre os arames distribui tensões decorrentes de variações de pressão no sistema.",
        },
        {
          title: "Formatos prontos para aplicação",
          text: "Disponível em rolos, folhas cortadas para filtros de folha, discos e cilindros calandrados e soldados.",
        },
      ],
    },
    related: {
      heading: "Telas especiais correlatas",
      intro: "Conheça outras variantes dentro de nossa linha de telas de arame especiais.",
      cards: [
        {
          title: "Telas metálicas tecidas especiais",
          text: "Visão abrangente de geometrias de tecido metálico para processos industriais críticos.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Linha principal",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Tela tecida de aço inoxidável de precisão especial",
          },
        },
        {
          title: "Tela tecida de alta resistência",
          text: "Arame espesso com ondulação travada para classificação de agregados e alta resistência ao desgaste abrasivo.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Alta resistência",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Tela de arame tecida pré-ondulada para serviço pesado",
          },
        },
        {
          title: "Tela tecida sob medida",
          text: "Aberturas personalizadas e combinações especiais de arame dimensionadas para equipamentos OEM.",
          to: "/CustomWovenMeshPage",
          badge: "Sob medida",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
            alt: "Tela de arame de aço inoxidável personalizada",
          },
        },
      ],
    },
    cta: {
      id: "five-heddle-cta",
      eyebrow: "Engenharia de filtração",
      heading: "Solicitar informações sobre tecelagem de cinco liços",
      lead: "Fale com nossa equipe técnica para definir malha, medidas das folhas e liga de aço inoxidável para o seu equipamento.",
      secondaryLabel: "Explorar telas especiais",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Folhas sob medida e rolos completos fornecidos sob consulta.",
    },
  },
};
